import { mapState, mapGetters, mapActions } from 'vuex'
import {
  MODULE,
  LOGIN,
  RESET_PASSWORD,
  UPDATE_PASSWORD,
  LOGOUT,
  REFRESH_TOKEN,
  STAFF_LOGIN,
  STAFF_RESET_PASSWORD,
  STAFF_UPDATE_PASSWORD,
  STAFF_LOGOUT,
  STAFF_REFRESH_TOKEN,
} from '@/state/modules/auth'
import {
  LOOKUP_MODULE,
  LOOKUP_ROLE,
  LOOKUP_USER,
  LOOKUP_AGENT,
  LOOKUP_UNLINKED_AGENT,
  LOOKUP_PERMISSION,
  LOOKUP_SALES_DOC_TYPE,
  LOOKUP_AREA_CODE,
  LOOKUP_ITEM,
  LOOKUP_DEBTOR,
  LOOKUP_SALES_DOC,
  LOOKUP_TAX,
} from '@/state/modules/lookup'
import { COMMUNICATION_LOG_MODULE, GET_ALL_COMMUNICATION_LOGS, RESEND_COMMUNICATION_LOG } from '@/state/modules/communicationLog'
import {
  CLIENT_GROUP_MODULE, GET_ALL_CLIENT_GROUPS, GET_CLIENT_GROUP,
  CREATE_CLIENT_GROUP, UPDATE_CLIENT_GROUP, DELETE_CLIENT_GROUP,
} from '@/state/modules/clientGroup'
import {
  LEAD_FORM_MODULE, GET_ALL_LEAD_FORMS, GET_LEAD_FORM, CREATE_LEAD_FORM,
  UPDATE_LEAD_FORM, DELETE_LEAD_FORM, ROTATE_LEAD_FORM_TOKEN,
  GET_LEAD_FORM_SUBMISSIONS, GET_LEAD_FORM_SCHEMA,
} from '@/state/modules/leadForm'
import {
  COMPANY_PROFILE_MODULE, GET_ALL_COMPANY_PROFILES, CREATE_COMPANY_PROFILE,
  UPDATE_COMPANY_PROFILE, SET_PRIMARY_COMPANY_PROFILE, DELETE_COMPANY_PROFILE,
} from '@/state/modules/companyProfile'
import { GLOBAL_FILTER_MODULE, SET_GLOBAL_FILTER } from '@/state/modules/globalFilter'
import {
  USERS_MODULE, GET_ALL_USER, GET_USERS, CREATE_USERS, UPDATE_USERS,
  UPDATE_USERS_STATUS, UPDATE_USERS_LOGIN_ACCESS, UNLOCK_USERS,
} from '@/state/modules/users'
import {
  ROLE_MODULE, GET_ALL_ROLES, GET_ROLE, CREATE_ROLE, UPDATE_ROLE, UPDATE_ROLE_STATUS,
} from '@/state/modules/role'
import {
  CONFIGURATION_MODULE, GET_ALL_CONFIGURATION, GET_CONFIGURATION, UPDATE_CONFIGURATION,
} from '@/state/modules/configuration'
import {
  USER_METADATA_MODULE, GET_ALL_USER_METADATA, APPROVE_USER_METADATA,
} from '@/state/modules/userMetadata'
import {
  AGENT_MODULE, GET_AGENTS, CREATE_AGENT, UPDATE_AGENT, DELETE_AGENT, SYNC_AGENTS,
} from '@/state/modules/agent'
import {
  ACCOUNTING_MODULE, GET_CONNECTION_STATUS, GET_CONNECT_URL, ACTIVATE_CONNECTION, DISCONNECT_CONNECTION,
} from '@/state/modules/accounting'

// Sales core modules
import {
  CATALOG_MODULE, GET_ALL_CATALOG, GET_CATALOG, GET_CATALOG_BY_ITEM_CODE,
  UPSERT_CATALOG_BY_ITEM_CODE,
  CREATE_CATALOG, UPDATE_CATALOG, DELETE_CATALOG, SYNC_CATALOG,
  GET_CAN_EXCHANGE_ITEMS, SET_CAN_EXCHANGE,
  GET_SHOWING_IN_SALES_DOCS_ITEMS, SET_IS_SHOWING_IN_SALES_DOCS,
  SET_CATALOG_IS_ACTIVE,
  GET_CATALOG_SYNC_STATUS, GET_CATALOG_UOMS,
  CREATE_CATALOG_UOM, UPDATE_CATALOG_UOM, DELETE_CATALOG_UOM,
} from '@/state/modules/catalog'
import { PRODUCT_MODULE, GET_ALL_PRODUCTS, GET_PRODUCT } from '@/state/modules/product'
import { CLIENT_MODULE, GET_ALL_CLIENTS, GET_CLIENT, CREATE_CLIENT, UPDATE_CLIENT } from '@/state/modules/client'
import { CLIENT_METADATA_MODULE, GET_CLIENT_METADATA, UPSERT_CLIENT_METADATA } from '@/state/modules/clientMetadata'
import {
  ITEM_PRICE_MODULE, GET_ALL_ITEM_PRICES, GET_ITEM_PRICE_BY_ITEM, CREATE_ITEM_PRICE,
} from '@/state/modules/itemPrice'
import {
  STOCK_BALANCE_MODULE, GET_ALL_STOCK_BALANCES, GET_STOCK_BALANCE_BY_ITEM,
} from '@/state/modules/stockBalance'
import {
  STOCK_OPERATION_MODULE, GET_ALL_STOCK_OPERATIONS, GET_STOCK_OPERATION,
} from '@/state/modules/stockOperation'
import {
  SALES_DOC_MODULE, GET_ALL_SALES_DOCS, GET_SALES_DOC, CREATE_SALES_DOC,
  UPDATE_SALES_DOC, DELETE_SALES_DOC, GET_CONSIGNMENT_INVENTORY,
  GET_CONSIGNMENT_BALANCE, CONVERT_SO_TO_SI, MARK_QUOTATION_WON, MARK_QUOTATION_LOST,
  GET_CUSTOMER_PRICE, GET_QUOTATION_STATUS,
} from '@/state/modules/salesDoc'
import {
  DELIVERY_MODULE, GET_ALL_DELIVERIES, GET_DELIVERY, CREATE_DELIVERY,
  UPDATE_DELIVERY, UPDATE_DELIVERY_STATUS, CREATE_DELIVERY_RETURN,
} from '@/state/modules/delivery'
import {
  PAYMENT_MODULE, GET_ALL_PAYMENTS, GET_PAYMENT,
} from '@/state/modules/payment'
import { AGING_MODULE, GET_AGING } from '@/state/modules/aging'

// Customer 360 + CRM modules
import { CUSTOMER_360_MODULE, GET_CUSTOMER_360, DOWNLOAD_STATEMENT } from '@/state/modules/customer360'
import {
  CLIENT_TAG_MODULE, GET_ALL_TAGS, CREATE_TAG, UPDATE_TAG, DELETE_TAG, ASSIGN_TAG, UNASSIGN_TAG,
} from '@/state/modules/clientTag'
import {
  CLIENT_ATTACHMENT_MODULE, GET_ATTACHMENTS, UPLOAD_ATTACHMENT, DELETE_ATTACHMENT,
} from '@/state/modules/clientAttachment'
import {
  LEAD_ACTIVITY_MODULE, GET_LEAD_ACTIVITIES, CREATE_LEAD_ACTIVITY,
  UPDATE_LEAD_ACTIVITY, DELETE_LEAD_ACTIVITY, COMPLETE_LEAD_ACTIVITY,
  GET_TODAY_ACTIVITIES, GET_OVERDUE_ACTIVITIES, GET_UPCOMING_ACTIVITIES, GET_CALENDAR_ACTIVITIES,
} from '@/state/modules/leadActivity'
import {
  OPPORTUNITY_MODULE, GET_ALL_OPPORTUNITIES, GET_OPPORTUNITY_PIPELINE, GET_OPPORTUNITY_STATS, GET_OPPORTUNITY,
  CREATE_OPPORTUNITY, UPDATE_OPPORTUNITY, DELETE_OPPORTUNITY,
  SET_OPPORTUNITY_STAGE, WIN_OPPORTUNITY, LOSE_OPPORTUNITY,
} from '@/state/modules/opportunity'
import { CRM_OPTION_MODULE, GET_CRM_OPTIONS, ADD_CRM_OPTION } from '@/state/modules/crmOption'
import {
  LEAD_MODULE, GET_ALL_LEADS, GET_LEAD_STATS, GET_LEAD, CREATE_LEAD,
  UPDATE_LEAD, DELETE_LEAD, CONVERT_LEAD, ASSIGN_LEAD,
} from '@/state/modules/lead'
import {
  CONTACT_MODULE, GET_CONTACTS, CREATE_CONTACT, UPDATE_CONTACT, DELETE_CONTACT,
} from '@/state/modules/contact'

// Sales management modules
import {
  SALES_TARGET_MODULE, GET_ALL_SALES_TARGETS, GET_SALES_TARGET,
  CREATE_SALES_TARGET, UPDATE_SALES_TARGET, DELETE_SALES_TARGET,
  GET_MY_PROGRESS, GET_ALL_PROGRESS, GET_BY_AGENT, GET_AGENT_DETAIL, GET_MONTHLY_SUMMARY,
} from '@/state/modules/salesTarget'
import {
  COMMISSION_MODULE, GET_ALL_COMMISSION_RATES, CREATE_COMMISSION_RATE,
  UPDATE_COMMISSION_RATE, DELETE_COMMISSION_RATE,
  GET_ALL_COMMISSION_RECORDS, GET_COMMISSION_SUMMARY, BATCH_UPDATE_COMMISSION_STATUS,
} from '@/state/modules/commission'
import {
  APPROVAL_MODULE, GET_ALL_APPROVALS, GET_APPROVAL,
  APPROVE_APPROVAL, REJECT_APPROVAL, DELETE_APPROVAL, GET_PENDING_COUNT, UPDATE_APPROVAL_PAYLOAD,
} from '@/state/modules/approval'
import {
  STORE_MODULE, GET_ALL_STORES, CREATE_STORE, UPDATE_STORE, DELETE_STORE,
} from '@/state/modules/store'
import {
  REPORT_MODULE, GET_SALES_SUMMARY, GET_TOP_DEBTORS, GET_MONTHLY_TREND, GET_WEEKLY_TREND,
  GET_SALES_BY_AGENT, GET_SALES_BY_PRODUCT, GET_SALES_BY_CLIENT_GROUP,
  GET_STOCK_BUFFER, GET_STOCK_MOVEMENT, GET_MARKET_STOCK, GET_AGENT_STOCK, GET_AGENT_STOCK_DETAIL,
  GET_RECENT_SALES_DOCS,
} from '@/state/modules/report'
import {
  PAYMENT_METHOD_MODULE, GET_ALL_PAYMENT_METHODS, CREATE_PAYMENT_METHOD,
  UPDATE_PAYMENT_METHOD, DELETE_PAYMENT_METHOD,
} from '@/state/modules/paymentMethod'
import {
  WALLET_MODULE, GET_MY_WALLET, GET_MY_WALLET_TRANSACTIONS, GET_ALL_WALLETS,
  GET_WALLET_TRANSACTIONS, WITHDRAW_FROM_WALLET, ADJUST_WALLET,
} from '@/state/modules/wallet'

export const authComputed = {
  ...mapState('auth', { currentUser: (state) => state.currentUser }),
  ...mapGetters('auth', ['loggedIn']),
  ...mapGetters('auth', ['staffLoggedIn']),
}

export const layoutComputed = {
  ...mapState('layout', {
    layoutType: (state) => state.layoutType,
    leftSidebarType: (state) => state.leftSidebarType,
    layoutWidth: (state) => state.layoutWidth,
    topbar: (state) => state.topbar,
    loader: (state) => state.loader,
  }),
}

export const layoutMethods = mapActions('layout', [
  'changeLayoutType', 'changeLayoutWidth', 'changeLeftSidebarType', 'changeTopbar', 'changeLoaderValue',
])

export const authMethods = mapActions(MODULE, [
  LOGIN, RESET_PASSWORD, UPDATE_PASSWORD, LOGOUT, REFRESH_TOKEN,
  STAFF_REFRESH_TOKEN, STAFF_LOGOUT, STAFF_UPDATE_PASSWORD, STAFF_RESET_PASSWORD, STAFF_LOGIN,
])

export const lookupMethods = mapActions(LOOKUP_MODULE, [LOOKUP_ROLE, LOOKUP_USER, LOOKUP_AGENT, LOOKUP_UNLINKED_AGENT, LOOKUP_PERMISSION, LOOKUP_SALES_DOC_TYPE, LOOKUP_AREA_CODE, LOOKUP_ITEM, LOOKUP_DEBTOR, LOOKUP_SALES_DOC, LOOKUP_TAX])
export const communicationLogMethods = mapActions(COMMUNICATION_LOG_MODULE, [GET_ALL_COMMUNICATION_LOGS, RESEND_COMMUNICATION_LOG])
export const clientGroupMethods = mapActions(CLIENT_GROUP_MODULE, [
  GET_ALL_CLIENT_GROUPS, GET_CLIENT_GROUP, CREATE_CLIENT_GROUP, UPDATE_CLIENT_GROUP, DELETE_CLIENT_GROUP,
])
export const leadFormMethods = mapActions(LEAD_FORM_MODULE, [
  GET_ALL_LEAD_FORMS, GET_LEAD_FORM, CREATE_LEAD_FORM, UPDATE_LEAD_FORM,
  DELETE_LEAD_FORM, ROTATE_LEAD_FORM_TOKEN, GET_LEAD_FORM_SUBMISSIONS, GET_LEAD_FORM_SCHEMA,
])
export const globalFilterMethods = mapActions(GLOBAL_FILTER_MODULE, [SET_GLOBAL_FILTER])
export const usersMethods = mapActions(USERS_MODULE, [GET_ALL_USER, GET_USERS, CREATE_USERS, UPDATE_USERS, UPDATE_USERS_STATUS, UPDATE_USERS_LOGIN_ACCESS, UNLOCK_USERS])
export const roleMethods = mapActions(ROLE_MODULE, [GET_ALL_ROLES, GET_ROLE, CREATE_ROLE, UPDATE_ROLE, UPDATE_ROLE_STATUS])
export const configurationMethods = mapActions(CONFIGURATION_MODULE, [GET_ALL_CONFIGURATION, GET_CONFIGURATION, UPDATE_CONFIGURATION])
export const userMetadataMethods = mapActions(USER_METADATA_MODULE, [GET_ALL_USER_METADATA, APPROVE_USER_METADATA])
export const agentMethods = mapActions(AGENT_MODULE, [GET_AGENTS, CREATE_AGENT, UPDATE_AGENT, DELETE_AGENT, SYNC_AGENTS])
export const accountingMethods = mapActions(ACCOUNTING_MODULE, [GET_CONNECTION_STATUS, GET_CONNECT_URL, ACTIVATE_CONNECTION, DISCONNECT_CONNECTION])

// Sales core methods
export const catalogMethods = mapActions(CATALOG_MODULE, [
  GET_ALL_CATALOG, GET_CATALOG, GET_CATALOG_BY_ITEM_CODE,
  UPSERT_CATALOG_BY_ITEM_CODE,
  CREATE_CATALOG, UPDATE_CATALOG, DELETE_CATALOG, SYNC_CATALOG,
  GET_CAN_EXCHANGE_ITEMS, SET_CAN_EXCHANGE,
  GET_SHOWING_IN_SALES_DOCS_ITEMS, SET_IS_SHOWING_IN_SALES_DOCS,
  SET_CATALOG_IS_ACTIVE,
  GET_CATALOG_SYNC_STATUS, GET_CATALOG_UOMS,
  CREATE_CATALOG_UOM, UPDATE_CATALOG_UOM, DELETE_CATALOG_UOM,
])
export const productMethods = mapActions(PRODUCT_MODULE, [GET_ALL_PRODUCTS, GET_PRODUCT])
export const clientMethods = mapActions(CLIENT_MODULE, [GET_ALL_CLIENTS, GET_CLIENT, CREATE_CLIENT, UPDATE_CLIENT])
export const clientMetadataMethods = mapActions(CLIENT_METADATA_MODULE, [GET_CLIENT_METADATA, UPSERT_CLIENT_METADATA])
export const itemPriceMethods = mapActions(ITEM_PRICE_MODULE, [GET_ALL_ITEM_PRICES, GET_ITEM_PRICE_BY_ITEM, CREATE_ITEM_PRICE])
export const stockBalanceMethods = mapActions(STOCK_BALANCE_MODULE, [GET_ALL_STOCK_BALANCES, GET_STOCK_BALANCE_BY_ITEM])
export const stockOperationMethods = mapActions(STOCK_OPERATION_MODULE, [GET_ALL_STOCK_OPERATIONS, GET_STOCK_OPERATION])
export const salesDocMethods = mapActions(SALES_DOC_MODULE, [
  GET_ALL_SALES_DOCS, GET_SALES_DOC, CREATE_SALES_DOC, UPDATE_SALES_DOC, DELETE_SALES_DOC,
  GET_CONSIGNMENT_INVENTORY, GET_CONSIGNMENT_BALANCE, CONVERT_SO_TO_SI,
  MARK_QUOTATION_WON, MARK_QUOTATION_LOST, GET_CUSTOMER_PRICE, GET_QUOTATION_STATUS,
])
export const deliveryMethods = mapActions(DELIVERY_MODULE, [
  GET_ALL_DELIVERIES, GET_DELIVERY, CREATE_DELIVERY, UPDATE_DELIVERY,
  UPDATE_DELIVERY_STATUS, CREATE_DELIVERY_RETURN,
])
export const paymentMethods = mapActions(PAYMENT_MODULE, [GET_ALL_PAYMENTS, GET_PAYMENT])
export const agingMethods = mapActions(AGING_MODULE, [GET_AGING])

// Customer 360 + CRM methods
export const customer360Methods = mapActions(CUSTOMER_360_MODULE, [GET_CUSTOMER_360, DOWNLOAD_STATEMENT])
export const clientTagMethods = mapActions(CLIENT_TAG_MODULE, [GET_ALL_TAGS, CREATE_TAG, UPDATE_TAG, DELETE_TAG, ASSIGN_TAG, UNASSIGN_TAG])
export const clientAttachmentMethods = mapActions(CLIENT_ATTACHMENT_MODULE, [GET_ATTACHMENTS, UPLOAD_ATTACHMENT, DELETE_ATTACHMENT])
export const leadActivityMethods = mapActions(LEAD_ACTIVITY_MODULE, [
  GET_LEAD_ACTIVITIES, CREATE_LEAD_ACTIVITY, UPDATE_LEAD_ACTIVITY, DELETE_LEAD_ACTIVITY, COMPLETE_LEAD_ACTIVITY,
  GET_TODAY_ACTIVITIES, GET_OVERDUE_ACTIVITIES, GET_UPCOMING_ACTIVITIES, GET_CALENDAR_ACTIVITIES,
])
export const crmOptionMethods = mapActions(CRM_OPTION_MODULE, [GET_CRM_OPTIONS, ADD_CRM_OPTION])
export const opportunityMethods = mapActions(OPPORTUNITY_MODULE, [
  GET_ALL_OPPORTUNITIES, GET_OPPORTUNITY_PIPELINE, GET_OPPORTUNITY_STATS, GET_OPPORTUNITY, CREATE_OPPORTUNITY,
  UPDATE_OPPORTUNITY, DELETE_OPPORTUNITY, SET_OPPORTUNITY_STAGE, WIN_OPPORTUNITY, LOSE_OPPORTUNITY,
])
export const leadMethods = mapActions(LEAD_MODULE, [GET_ALL_LEADS, GET_LEAD_STATS, GET_LEAD, CREATE_LEAD, UPDATE_LEAD, DELETE_LEAD, CONVERT_LEAD, ASSIGN_LEAD])
export const contactMethods = mapActions(CONTACT_MODULE, [GET_CONTACTS, CREATE_CONTACT, UPDATE_CONTACT, DELETE_CONTACT])

// Sales management methods
export const salesTargetMethods = mapActions(SALES_TARGET_MODULE, [
  GET_ALL_SALES_TARGETS, GET_SALES_TARGET, CREATE_SALES_TARGET,
  UPDATE_SALES_TARGET, DELETE_SALES_TARGET, GET_MY_PROGRESS, GET_ALL_PROGRESS,
  GET_BY_AGENT, GET_AGENT_DETAIL, GET_MONTHLY_SUMMARY,
])
export const commissionMethods = mapActions(COMMISSION_MODULE, [
  GET_ALL_COMMISSION_RATES, CREATE_COMMISSION_RATE, UPDATE_COMMISSION_RATE, DELETE_COMMISSION_RATE,
  GET_ALL_COMMISSION_RECORDS, GET_COMMISSION_SUMMARY, BATCH_UPDATE_COMMISSION_STATUS,
])
export const approvalMethods = mapActions(APPROVAL_MODULE, [
  GET_ALL_APPROVALS, GET_APPROVAL, APPROVE_APPROVAL, REJECT_APPROVAL, DELETE_APPROVAL,
  GET_PENDING_COUNT, UPDATE_APPROVAL_PAYLOAD,
])
export const storeMethods = mapActions(STORE_MODULE, [
  GET_ALL_STORES, CREATE_STORE, UPDATE_STORE, DELETE_STORE,
])
export const companyProfileMethods = mapActions(COMPANY_PROFILE_MODULE, [
  GET_ALL_COMPANY_PROFILES, CREATE_COMPANY_PROFILE, UPDATE_COMPANY_PROFILE,
  SET_PRIMARY_COMPANY_PROFILE, DELETE_COMPANY_PROFILE,
])
export const reportMethods = mapActions(REPORT_MODULE, [
  GET_SALES_SUMMARY, GET_TOP_DEBTORS, GET_MONTHLY_TREND, GET_WEEKLY_TREND,
  GET_SALES_BY_AGENT, GET_SALES_BY_PRODUCT, GET_SALES_BY_CLIENT_GROUP,
  GET_STOCK_BUFFER, GET_STOCK_MOVEMENT, GET_MARKET_STOCK, GET_AGENT_STOCK, GET_AGENT_STOCK_DETAIL,
  GET_RECENT_SALES_DOCS,
])
export const paymentMethodMethods = mapActions(PAYMENT_METHOD_MODULE, [
  GET_ALL_PAYMENT_METHODS, CREATE_PAYMENT_METHOD, UPDATE_PAYMENT_METHOD, DELETE_PAYMENT_METHOD,
])
export const walletMethods = mapActions(WALLET_MODULE, [
  GET_MY_WALLET, GET_MY_WALLET_TRANSACTIONS, GET_ALL_WALLETS, GET_WALLET_TRANSACTIONS,
  WITHDRAW_FROM_WALLET, ADJUST_WALLET,
])
